/**
 * HomeTrustStrip — compact live-signals band displayed directly under the
 * full-screen hero on the home page. Single horizontal row on desktop
 * (stacks on mobile) showing four conversion-critical trust elements:
 *
 *   1. Tree-Nation live tree counter (real-time from public registry)
 *   2. Trustpilot Mini Star widget (hydrates once Trustpilot verifies domain)
 *   3. India 5000 Best MSME 2024 award badge
 *   4. "Carbon-neutral since 2023" timestamped commitment
 *
 * The Tree-Nation + Trustpilot bootstrap scripts are already lazy-injected
 * once by the Footer component (which renders on every page), so this
 * component does NOT inject anything itself — it just emits the markup the
 * existing scripts scan for.
 *
 * Reveal animation is driven by the shared `useRevealOnScroll` hook +
 * `.reveal-stagger` / `.reveal-item` utility classes in src/index.css (same
 * pattern reused by the Signature Collections grid below).
 */
import { Award } from "lucide-react";
import { useEffect, useState } from "react";
import useRevealOnScroll from "../../lib/useRevealOnScroll";
import TrustpilotWidget from "./TrustpilotWidget";
import api from "../../lib/api";

export default function HomeTrustStrip() {
    const { ref, visible } = useRevealOnScroll();
    const [tp, setTp] = useState(null);
    useEffect(() => {
        api.get("/settings/public").then((r) => setTp(r.data)).catch(() => {});
    }, []);
    return (
        <section
            ref={ref}
            className={`bg-secondary border-y border-border reveal-stagger ${visible ? "is-visible" : ""}`}
            data-testid="home-trust-strip"
            aria-label="Trust signals — live forest counter, Trustpilot rating, India 5000 award, carbon-neutral since 2023"
        >
            <div className="container-page py-5">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-4 md:gap-y-0 items-center divide-x-0 md:divide-x divide-border">
                    {/* TILE 1 — Tree-Nation live counter */}
                    <div className="reveal-item md:px-6 first:pl-0 flex items-center justify-center min-h-[44px]" data-testid="trust-strip-trees">
                        <div
                            data-widget-type="tree-counter"
                            data-tree-nation-code="275f463178ff5fda"
                            data-lang="en"
                            data-theme="light"
                        ></div>
                    </div>

                    {/* TILE 2 — Trustpilot brand badge + admin-set score */}
                    <div className="reveal-item md:px-6 flex items-center justify-center gap-3 min-h-[44px]" data-testid="trust-strip-trustpilot">
                        <TrustpilotWidget starSize={18} className="!flex-row !items-center !gap-3 !py-2" />
                        {tp?.trustpilot_enabled && tp?.trustpilot_score ? (
                            <span className="text-[11px] leading-tight" data-testid="trust-strip-tp-score">
                                <span className="block font-semibold text-foreground">Rated {tp.trustpilot_score} / 5</span>
                                {tp.trustpilot_reviews ? <span className="block text-muted-foreground tracking-wide">{tp.trustpilot_reviews} reviews</span> : null}
                            </span>
                        ) : null}
                    </div>

                    {/* TILE 3 — India 5000 award badge */}
                    <div className="reveal-item md:px-6 flex items-center gap-3 justify-center md:justify-start min-h-[44px]" data-testid="trust-strip-award">
                        <Award size={22} strokeWidth={1.5} className="text-accent shrink-0" />
                        <div className="text-[11px] leading-tight">
                            <div className="font-semibold text-foreground">India 5000 MSME</div>
                            <div className="text-muted-foreground tracking-wide">Winner · 2024</div>
                        </div>
                    </div>

                    {/* TILE 4 — Carbon-neutral commitment */}
                    <div className="reveal-item md:px-6 last:pr-0 flex items-center gap-3 justify-center md:justify-start min-h-[44px]" data-testid="trust-strip-carbon">
                        <div className="shrink-0 w-8 h-8 rounded-full border border-accent/40 flex items-center justify-center text-accent text-xs font-semibold">
                            CO<sub>2</sub>
                        </div>
                        <div className="text-[11px] leading-tight">
                            <div className="font-semibold text-foreground">Carbon-neutral</div>
                            <div className="text-muted-foreground tracking-wide">Since 2023 · audited</div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
